import React, { useState, useEffect, useRef } from"react";
import { useLocation, useNavigate } from"react-router-dom";
import useSkillGenieSession from"../hooks/useSkillGenieSession";
import API_BASE_URL from"../Config/config";
import FeatureGuard from"./access/FeatureGuard";
import TutorShell, { RailSection, RailStat, RailTip } from "./aiTutor/TutorShell";
import { useTutorLanguage, LanguagePicker } from "./aiTutor/languages";
import {
  TutorMessages,
  TutorComposer,
  TutorBadge,
  TutorEmptyState,
} from "./aiTutor/TutorChat";
import NexusUsageBadge from "../NextGenBuilder/components/NexusUsageBadge";
import useNexusLearnerBilling from "../NextGenBuilder/hooks/useNexusLearnerBilling";

const ExplainPage = ({ mode = "learner" }) => {
 const isTenantMode = mode === "tenant";

 // Tenant mode bypasses feature guard
 if (isTenantMode) {
   return <ExplainPageContent mode={mode} />;
 }

 // Learner mode requires TSG_AI access
 return (
   <FeatureGuard feature="TSG_AI">
     <ExplainPageContent mode={mode} />
   </FeatureGuard>
 );
};

const ExplainPageContent = ({ mode = "learner" }) => {
  const [input, setInput] = useState("");
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const chatEndRef = useRef(null);
  const navigate = useNavigate();

  const isTenantMode = mode === "tenant";

  const location = useLocation();
  const { session } = useSkillGenieSession();

  // TSG Nexus feature billing. Inert (isNexus === false) in the learner app
  // and in college portals, so nothing below changes for them.
  const nexus = useNexusLearnerBilling("AI_TUTOR", { subFeature: "EXPLAIN_DEEP" });

  // Prefer language from navigation state, then session, then default 'en'
  const { langCode, langName, setLanguage } = useTutorLanguage({
    initialLanguage: location.state?.language || session?.language,
  });
  // Keep the original local name so the rest of this file is untouched.
  const language = langCode;
  const technology = location.state?.technology || "general";
  const topic = location.state?.topic || "general";

  const handleAsk = async () => {
    if (!input.trim()) return;

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userId = user?.userId || user?.user_id || localStorage.getItem("user_id") || "UnknownUser";

    if (!userId || userId === "UnknownUser") {
      alert("Please log in to use this feature.");
      navigate("/login");
      return;
    }

    const currentInput = input;
    setChat((prev) => [...prev, { role: "user", content: currentInput }]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/tsg/explain_deep`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "user-id": userId,
          ...nexus.headers(),
        },
        body: JSON.stringify({
          userId,
          prompt: currentInput,
          chatHistory: chat,
          language,
          technology,
          topic,
        }),
      });

      const data = await res.json();

      // TSG Nexus: the builder is out of free prompts and paid credits. Open
      // the purchase modal instead of the legacy premium modal.
      if (res.status === 402 && nexus.handleQuota(data)) {
        setChat((prev) => [
          ...prev,
          { role: "assistant", content: "You've used all your free Explain Deep prompts for this month. Recharge to continue." },
        ]);
        return;
      }

      nexus.noteUsage(res);

      const reply = data.reply || "No reply.";
      setChat((prev) => [
        ...prev,
        { role: "assistant", content: reply },
      ]);
    } catch {
      setChat((prev) => [
        ...prev,
        { role: "assistant", content: "❌ Error reaching TSG." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const container = chatEndRef.current?.parentElement;
    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (isAtBottom || chat.length <= 2) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [chat]);

  const BubbleIcon = (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );

  // Starters are plain input prefills — they do not send, so nobody loses a
  // turn to a misclick. Purely additive: the free-text box behaves as before.
  const STARTERS = [
    "Explain how JWT authentication works, end to end",
    "What is the difference between SQL and NoSQL, with examples?",
    "Walk me through the React rendering lifecycle",
    "Explain Big-O notation like I am new to it",
  ];

  return (
    <>
      <TutorShell
        language={<LanguagePicker value={langCode} onChange={setLanguage} />}
        icon={BubbleIcon}
        title="Explain Deep"
        subtitle="Ask any complex topic and get a deep, step-by-step explanation you can work through at your own pace."
        onBack={() => navigate(-1)}
        headerExtra={nexus.isNexus ? <NexusUsageBadge feature="AI_TUTOR" subFeature="EXPLAIN_DEEP" /> : null}
        badges={
          <>
            <TutorBadge label="Mode" value={technology} />
            <TutorBadge label="Lang" value={langName} dot="#22C55E" />
          </>
        }
        rail={
          <>
            <RailSection title="Session">
              <RailStat label="Technology" value={technology} />
              <RailStat label="Topic" value={topic} />
              <RailStat label="Language" value={langName} hint="Answers are returned in this language." />
              <RailStat label="Turns" value={String(Math.ceil(chat.length / 2))} hint="Each answer keeps the whole thread as context." />
            </RailSection>
            <RailSection title="Try asking">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  className="w-full text-left rounded-2xl border border-[#E9E7F3] bg-white px-4 py-3 mb-2 last:mb-0 hover:border-[#5B21B6] hover:bg-[#F5F2FF] transition-all"
                >
                  <span className="text-[12px] font-semibold text-[#4B3F72] leading-snug">{s}</span>
                </button>
              ))}
            </RailSection>
            <RailSection title="Getting better answers">
              <RailTip>Say what you already understand — the explanation starts from there instead of the basics.</RailTip>
              <RailTip>Ask follow-ups in the same thread. The full conversation is sent as context each time.</RailTip>
            </RailSection>
          </>
        }
        composer={
          <TutorComposer
            value={input}
            onChange={setInput}
            onSubmit={handleAsk}
            loading={loading}
            disabled={!input.trim()}
            placeholder="Ask me anything..."
            hint={nexus.hint}
          />
        }
      >
        <TutorMessages
          chat={chat}
          chatEndRef={chatEndRef}
          empty={
            <TutorEmptyState
              icon={<svg className="w-9 h-9 text-[#5B21B6]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>}
              title="How can I help you today?"
              description="Ask any complex topic and I will explain it deeply — concepts, trade-offs, and worked examples."
            >
              <div className="grid sm:grid-cols-2 gap-2">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    className="text-left rounded-2xl border border-[#E9E7F3] bg-white px-4 py-3 hover:border-[#5B21B6] hover:bg-[#F5F2FF] transition-all"
                  >
                    <span className="text-[13px] font-semibold text-[#4B3F72] leading-snug">{s}</span>
                  </button>
                ))}
              </div>
            </TutorEmptyState>
          }
        />
      </TutorShell>

      {/* Payment Modal — lifted from the previous markup unchanged. */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border border-primary-purple/40 rounded-3xl shadow-2xl p-8 text-center animate-in zoom-in-95 duration-300">
            <div className="w-20 h-20 bg-primary-purple/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary-purple/30">
              <span className="text-4xl">💎</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Premium Feature</h2>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Unlock the full potential of TSG Deep Explanation with our premium learning tracks.
            </p>
            <div className="space-y-4">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  navigate("/subscription-plans");
                }}
                className="w-full bg-primary-purple text-white font-bold py-4 rounded-xl hover:shadow-[0_0_20px_rgba(91,33,182,0.4)] transition-all transform hover:-translate-y-0.5"
              >
                View Plans →
              </button>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="w-full text-gray-500 hover:text-white transition-colors text-sm font-medium"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExplainPage;



